import { GetProviderPatientTranscriptionConsentQuery } from '../../generated/graphql';

import { faPenLine } from '@fortawesome/pro-solid-svg-icons';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect } from 'react';

import { Icon, Text } from '@growtherapy/sprout-ui';

import { useShouldDisableTranscriptionForMultipleParticipants } from '../../session/controls/useShouldDisableTranscriptionForMultipleParticipants';
import { patientConsentAtom } from '../../session/messages/state';
import { toast } from '../../toasts';
import { ConsentStatus, ConsentUpdateSource, roomAtom } from '../../video-sdk';
import { CLIENT_CONSENT_PROMPT_TOAST_DURATION } from './constants';
import { useCanProviderUseTranscriptionCallback } from './useCanProviderUseTranscriptionCallback';
import { useClientConsentPromptToasts } from './useClientConsentPromptToasts';

const TOAST_KEY = 'client-consent-prompt';

/**
 * Hook that returns a callback function to show a toast prompting the provider
 * to request transcription consent from a client.
 */
export function useClientConsentPromptToastCallback() {
  const room = useAtomValue(roomAtom);
  const { shouldShowToastForClient, recordToastForClient } = useClientConsentPromptToasts();
  const shouldShowMultipleParticipantTranscriptionDisabled = useShouldDisableTranscriptionForMultipleParticipants();
  const getPatientConsent = useAtomCallback(useCallback((get) => get(patientConsentAtom).value, []));
  const getCanProviderUseTranscription = useCanProviderUseTranscriptionCallback();

  useEffect(
    function clearToastOnRoomDisconnectOrMultiParticipant() {
      // If the session ends or a second participant joins the room and we don't
      // support transcription for multiple participants, remove the toast.
      if (!room || shouldShowMultipleParticipantTranscriptionDisabled) {
        toast.close(TOAST_KEY);
      }
    },
    [room, shouldShowMultipleParticipantTranscriptionDisabled],
  );

  return useCallback(
    (
      consentResponse: GetProviderPatientTranscriptionConsentQuery,
      patientShortId: string,
      appointmentShortId?: string,
    ) => {
      if (!appointmentShortId || !getCanProviderUseTranscription()) return;
      // If the provider is actively requesting client consent, we don't need to
      // show the toast prompt.
      if (getPatientConsent() === ConsentStatus.DECIDING) return;

      const patientConsent = !!consentResponse.clientTranscriptionConsent?.isOptedIn;
      const providerConsent = !!consentResponse.providerTranscriptionConsent?.isOptedIn;

      const patientConsentUpdateSource = consentResponse.clientTranscriptionConsent?.updateSource;
      const isTranscriptionOptedOutInClientPortal =
        patientConsentUpdateSource === ConsentUpdateSource.CLIENT_PORTAL && !patientConsent;

      // If either the provider has not consented to transcription, or the
      // client already has, we don't need to show the toast.
      if (!providerConsent || patientConsent) {
        return;
      }

      if (isTranscriptionOptedOutInClientPortal) {
        // If patient has explicitly opted out in the client_portal
        // we don't need to show the toast
        return;
      }
      if (!shouldShowToastForClient(patientShortId, appointmentShortId)) {
        return;
      }

      recordToastForClient(patientShortId, appointmentShortId);
      toast.info(
        <Text as='span' className='arc:text-text-inverse'>
          Get session notes by pressing the <span className='sr-only'>request transcription consent</span>
          <Icon aria-hidden icon={faPenLine} /> button below — your client will be asked to consent first.
        </Text>,
        {
          key: TOAST_KEY,
          timeout: CLIENT_CONSENT_PROMPT_TOAST_DURATION,
        },
      );
    },
    [shouldShowToastForClient, recordToastForClient, getPatientConsent, getCanProviderUseTranscription],
  );
}

/**
 * Hook that returns a callback function to close the client consent prompt
 * toast, if it is currently visible.
 */
export function useCloseClientConsentPromptToastCallback() {
  return useCallback(() => {
    toast.close(TOAST_KEY);
  }, []);
}
